import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom/client';
import { createPortal } from 'react-dom';
import { InstantSearch, Configure, } from 'react-instantsearch';
import SearchkitClient from '@searchkit/instantsearch-client';
import { states } from './helpers/mappers';
import SearchBox from './containers/searching/SearchBox';
import Autocomplete from './containers/searching/Autocomplete';
import Hits from './containers/results/Hits';
import HitsPerPage from './containers/pagination/HitsPerPage';
import Pagination from './containers/pagination/Pagination';
import SortBy from './containers/sorting/SortBy';
import Stats from './containers/metadata/Stats';
import RefinementList from './containers/refinements/RefinementList';
import ToggleRefinement from './containers/refinements/ToggleRefinement';
import Menu from './containers/refinements/Menu';
import HierarchicalMenu from './containers/refinements/HierarchicalMenu';
import RangeSlider from './containers/refinements/RangeSlider';
import ClearRefinements from './containers/refinements/ClearRefinements';
import CurrentRefinements from './containers/refinements/CurrentRefinements';
import JwtRefresher from './components/utils/JwtRefresher';
import CallbacksHandler from './containers/results/CallbacksHandler';
export default class WhiteInstantSearch {
    constructor(identity, searchClientConfig, searchClient, hitsPerPage, facetFilters, filters, additionalData, routing, routingSettings, scrolling, callbacks, searchBoxComponent, autocompleteComponent, hitComponent, hitsPerPageComponent, paginationComponent, sortByComponent, statsComponent, refinementsComponent, clearRefinementsComponent, currentRefinementsComponent, customContainers) {
        this.instantSearchApi = {
            setIndexUiState: null,
            setUiState: null,
            refresh: null,
        };
        this.identity = identity;
        this.searchClientConfig = searchClientConfig;
        this.searchClient = searchClient;
        this.hitsPerPage = hitsPerPage;
        this.facetFilters = facetFilters;
        this.filters = filters;
        this.additionalData = additionalData;
        this.routing = routing;
        this.routingSettings = routingSettings;
        this.scrolling = scrolling;
        this.callbacks = callbacks;
        this.searchBoxComponent = searchBoxComponent;
        this.autocompleteComponent = autocompleteComponent;
        this.hitComponent = hitComponent;
        this.hitsPerPageComponent = hitsPerPageComponent;
        this.paginationComponent = paginationComponent;
        this.sortByComponent = sortByComponent;
        this.statsComponent = statsComponent;
        this.refinementsComponent = refinementsComponent;
        this.clearRefinementsComponent = clearRefinementsComponent;
        this.currentRefinementsComponent = currentRefinementsComponent;
        this.customContainers = customContainers;
    }
    setIndexUiState(callback) {
        if (this.instantSearchApi.setIndexUiState) {
            this.instantSearchApi.setIndexUiState(callback);
        }
    }
    setUiState(callback) {
        if (this.instantSearchApi.setUiState) {
            this.instantSearchApi.setUiState(callback);
        }
    }
    refresh() {
        if (this.instantSearchApi.refresh) {
            this.instantSearchApi.refresh();
        }
    }
    show() {
        var _a, _b;
        const instantSearch = WhiteInstantSearch.ensureElement(this.identity);
        const scrollTo = ((_a = this.scrolling) === null || _a === void 0 ? void 0 : _a.element)
            ? document.querySelector((_b = this.scrolling) === null || _b === void 0 ? void 0 : _b.element)
            : null;
        const whiteInstantSearch = this;
        function RootComponent() {
            const [currentSearchClient, setCurrentSearchClient,] = useState(whiteInstantSearch.searchClient);
            const handleScrolling = useCallback((oldState, newState) => {
                const scrollingSettings = whiteInstantSearch.scrolling;
                if (!scrollingSettings) {
                    return;
                }
                const flatOldState = states.flatUiState(oldState);
                const flatNewState = states.flatUiState(newState);
                if (scrollingSettings.includeAttributes) {
                    Object.keys(flatOldState).forEach((attribute) => {
                        var _a;
                        if (!((_a = scrollingSettings.includeAttributes) === null || _a === void 0 ? void 0 : _a.includes(attribute))) {
                            delete flatOldState[attribute];
                        }
                    });
                    Object.keys(flatNewState).forEach((attribute) => {
                        var _a;
                        if (!((_a = scrollingSettings.includeAttributes) === null || _a === void 0 ? void 0 : _a.includes(attribute))) {
                            delete flatNewState[attribute];
                        }
                    });
                }
                if (JSON.stringify(flatOldState) !== JSON.stringify(flatNewState)) {
                    if (scrollTo) {
                        scrollTo.scrollIntoView({ behavior: 'smooth' });
                    }
                    else {
                        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                    }
                }
            }, []);
            const isSearchkit = whiteInstantSearch.searchClientConfig['type'] === 'searchkit';
            let lastUiState = null;
            const jwtRefresher = isSearchkit ? (React.createElement(JwtRefresher, { initialToken: whiteInstantSearch.searchClientConfig['jwt'], cookieName: "elasticsearch_token", checkIntervalSeconds: 5, refreshTimeBeforeExpirySeconds: 30, refreshIframeUrl: whiteInstantSearch.searchClientConfig['refreshIframeUrl'], onRefreshed: (token) => {
                    const config = {
                        url: whiteInstantSearch.searchClientConfig['url'],
                        headers: {
                            authorization: `Bearer ${token}`,
                        },
                    };
                    const searchKitClient = Object.assign(SearchkitClient(config), {
                        transporter: {
                            headers: {
                                'x-algolia-application-id': 'NULL',
                                'x-algolia-api-key': 'NULL',
                            },
                        },
                    });
                    setCurrentSearchClient(searchKitClient);
                } })) : null;
            if (!currentSearchClient) {
                return jwtRefresher;
            }
            const configureProps = {
                hitsPerPage: whiteInstantSearch.hitsPerPage,
                filters: whiteInstantSearch.filters,
                facetFilters: whiteInstantSearch.facetFilters,
            };
            return (React.createElement(React.Fragment, null,
                jwtRefresher,
                React.createElement(InstantSearch, { indexName: whiteInstantSearch.searchClientConfig['indexName'], searchClient: currentSearchClient, routing: whiteInstantSearch.routing, future: {
                        preserveSharedStateOnUnmount: true,
                    }, onStateChange: ({ uiState, setUiState }) => {
                        handleScrolling(lastUiState, uiState);
                        lastUiState = uiState;
                        setUiState(uiState);
                        whiteInstantSearch.callbacks.onStateChange.forEach((callback) => (callback(uiState)));
                    } },
                    React.createElement(Configure, Object.assign({}, configureProps)),
                    React.createElement(CallbacksHandler, { callbacks: whiteInstantSearch.callbacks, instantSearchApi: whiteInstantSearch.instantSearchApi }),
                    Object.keys(whiteInstantSearch.hitComponent).map((selector) => {
                        const hitList = whiteInstantSearch.hitComponent[selector];
                        const elements = selector ? [...document.querySelectorAll(selector)] : [];
                        const showComponent = hitList
                            && elements
                            && hitList.length > 0
                            && elements.length > 0;
                        if (!showComponent) {
                            return null;
                        }
                        return elements.map((element) => {
                            const { parentNode } = element;
                            if (!parentNode) {
                                return null;
                            }
                            const placeholder = document.createElement('div');
                            placeholder.style.display = 'none';
                            parentNode.insertBefore(placeholder, element.nextSibling);
                            setTimeout(() => {
                                placeholder.style.removeProperty('display');
                                placeholder.id = element.id;
                                element.classList.forEach((className) => {
                                    placeholder.classList.add(className);
                                });
                                element.replaceWith(placeholder);
                            }, 0);
                            return createPortal(React.createElement(React.Fragment, null, hitList.map((hit, index) => (React.createElement(Hits, { key: `${JSON.stringify({ [selector]: index })}`, hitComponent: hit.component, loadingComponent: hit.loadingComponent, noResultsComponent: hit.noResultsComponent, hitsSettings: hit.settings, additionalData: whiteInstantSearch.additionalData })))), placeholder);
                        });
                    }),
                    Object.keys(whiteInstantSearch.searchBoxComponent).map((selector) => {
                        const searchBoxList = whiteInstantSearch.searchBoxComponent[selector];
                        const elements = WhiteInstantSearch.getElements(selector);
                        const showComponent = searchBoxList
                            && elements
                            && searchBoxList.length > 0
                            && elements.length > 0;
                        if (!showComponent) {
                            return null;
                        }
                        return elements.map((element) => (createPortal(React.createElement(React.Fragment, null, searchBoxList.map((searchBox, index) => (React.createElement(SearchBox, { key: `${JSON.stringify({ [selector]: index })}`, searchBoxComponent: searchBox.component, searchBoxSettings: searchBox.settings, additionalData: whiteInstantSearch.additionalData })))), element)));
                    }),
                    Object.keys(whiteInstantSearch.autocompleteComponent).map((selector) => {
                        const autocompleteList = whiteInstantSearch.autocompleteComponent[selector];
                        const elements = WhiteInstantSearch.getElements(selector);
                        const showComponent = autocompleteList
                            && elements
                            && autocompleteList.length > 0
                            && elements.length > 0;
                        if (!showComponent) {
                            return null;
                        }
                        return elements.map((element) => (createPortal(React.createElement(React.Fragment, null, autocompleteList.map((autocomplete, index) => (React.createElement(Autocomplete, { key: `${JSON.stringify({ [selector]: index })}`, searchClient: currentSearchClient, defaultIndexName: whiteInstantSearch.searchClientConfig['indexName'], defaultFilters: whiteInstantSearch.filters, sources: autocomplete.settings.sources, useRecentSearches: autocomplete.settings.useRecentSearches, recentSearchesOptions: autocomplete.settings.recentSearchesOptions, detachedMediaQuery: autocomplete.settings.detachedMediaQuery, panelPlacement: autocomplete.settings.panelPlacement, classNames: autocomplete.settings.classNames, translations: autocomplete.settings.translations, placeholder: autocomplete.settings.placeholder, autoFocus: autocomplete.settings.autoFocus, openOnFocus: autocomplete.settings.openOnFocus, closeOnBlur: autocomplete.settings.closeOnBlur, additionalData: whiteInstantSearch.additionalData, onSubmit: autocomplete.settings.onSubmit
                                ? (params) => autocomplete.settings.onSubmit
                                    && autocomplete.settings.onSubmit(params.state.query)
                                : undefined })))), element)));
                    }),
                    Object.keys(whiteInstantSearch.hitsPerPageComponent).map((selector) => {
                        const hitsPerPageList = whiteInstantSearch.hitsPerPageComponent[selector];
                        const elements = WhiteInstantSearch.getElements(selector);
                        const showComponent = hitsPerPageList
                            && elements
                            && hitsPerPageList.length > 0
                            && elements.length > 0;
                        if (!showComponent) {
                            return null;
                        }
                        return elements.map((element) => (createPortal(React.createElement(React.Fragment, null, hitsPerPageList.map((hitsPerPage, index) => (React.createElement(HitsPerPage, { key: `${JSON.stringify({ [selector]: index })}`, hitsPerPageComponent: hitsPerPage.component, hitsPerPageSettings: hitsPerPage.settings, additionalData: whiteInstantSearch.additionalData })))), element)));
                    }),
                    Object.keys(whiteInstantSearch.paginationComponent).map((selector) => {
                        const paginationList = whiteInstantSearch.paginationComponent[selector];
                        const elements = WhiteInstantSearch.getElements(selector);
                        const showComponent = paginationList
                            && elements
                            && paginationList.length > 0
                            && elements.length > 0;
                        if (!showComponent) {
                            return null;
                        }
                        return elements.map((element) => (createPortal(React.createElement(React.Fragment, null, paginationList.map((pagination, index) => (React.createElement(Pagination, { key: `${JSON.stringify({ [selector]: index })}`, paginationComponent: pagination.component, paginationSettings: pagination.settings, additionalData: whiteInstantSearch.additionalData })))), element)));
                    }),
                    Object.keys(whiteInstantSearch.sortByComponent).map((selector) => {
                        const sortByList = whiteInstantSearch.sortByComponent[selector];
                        const elements = WhiteInstantSearch.getElements(selector);
                        const showComponent = sortByList
                            && elements
                            && sortByList.length > 0
                            && elements.length > 0;
                        if (!showComponent) {
                            return null;
                        }
                        return elements.map((element) => (createPortal(React.createElement(React.Fragment, null, sortByList.map((sortBy, index) => (React.createElement(SortBy, { key: `${JSON.stringify({ [selector]: index })}`, sortByComponent: sortBy.component, sortBySettings: sortBy.settings, additionalData: whiteInstantSearch.additionalData })))), element)));
                    }),
                    Object.keys(whiteInstantSearch.statsComponent).map((selector) => {
                        const statsList = whiteInstantSearch.statsComponent[selector];
                        const elements = WhiteInstantSearch.getElements(selector);
                        const showComponent = statsList
                            && elements
                            && statsList.length > 0
                            && elements.length > 0;
                        if (!showComponent) {
                            return null;
                        }
                        return elements.map((element) => (createPortal(React.createElement(React.Fragment, null, statsList.map((stats, index) => (React.createElement(Stats, { key: `${JSON.stringify({ [selector]: index })}`, statsComponent: stats.component, statsSettings: stats.settings, additionalData: whiteInstantSearch.additionalData })))), element)));
                    }),
                    Object.keys(whiteInstantSearch.refinementsComponent).map((selector) => {
                        const refinements = whiteInstantSearch.refinementsComponent[selector];
                        const elements = WhiteInstantSearch.getElements(selector);
                        const showComponent = refinements
                            && elements
                            && refinements.length > 0
                            && elements.length > 0;
                        if (!showComponent) {
                            return null;
                        }
                        return elements.map((element) => (createPortal(React.createElement(React.Fragment, null, refinements.map((refinement) => {
                            if (refinement.type === 'refinement-list') {
                                const settings = refinement.settings;
                                const component = refinement.component;
                                return (React.createElement(RefinementList, { key: settings.attribute, refinementListComponent: component, refinementListSettings: settings, additionalData: whiteInstantSearch.additionalData }));
                            }
                            if (refinement.type === 'toggle-refinement') {
                                const settings = refinement.settings;
                                const component = refinement.component;
                                return (React.createElement(ToggleRefinement, { key: settings.attribute, toggleRefinementComponent: component, toggleRefinementSettings: settings, additionalData: whiteInstantSearch.additionalData }));
                            }
                            if (refinement.type === 'menu') {
                                const settings = refinement.settings;
                                const component = refinement.component;
                                return (React.createElement(Menu, { key: settings.attribute, menuComponent: component, menuSettings: settings, routing: whiteInstantSearch.routingSettings, additionalData: whiteInstantSearch.additionalData }));
                            }
                            if (refinement.type === 'hierarchical-menu') {
                                const settings = refinement.settings;
                                const component = refinement.component;
                                return (React.createElement(HierarchicalMenu, { key: settings.attributes.join('-'), hierarchicalMenuComponent: component, menuSettings: settings, routing: whiteInstantSearch.routingSettings, additionalData: whiteInstantSearch.additionalData }));
                            }
                            if (refinement.type === 'range-slider') {
                                const settings = refinement.settings;
                                const component = refinement.component;
                                return (React.createElement(RangeSlider, { key: settings.attribute, rangeSliderComponent: component, sliderSettings: settings, additionalData: whiteInstantSearch.additionalData }));
                            }
                            return null;
                        })), element)));
                    }),
                    Object.keys(whiteInstantSearch.clearRefinementsComponent).map((selector) => {
                        const clearRefinementsList = whiteInstantSearch.clearRefinementsComponent[selector];
                        const elements = WhiteInstantSearch.getElements(selector);
                        const showComponent = clearRefinementsList
                            && elements
                            && clearRefinementsList.length > 0
                            && elements.length > 0;
                        if (!showComponent) {
                            return null;
                        }
                        return elements.map((element) => (createPortal(React.createElement(React.Fragment, null, clearRefinementsList.map((clearRefinements, index) => (React.createElement(ClearRefinements, { key: `${JSON.stringify({ [selector]: index })}`, clearRefinementsComponent: clearRefinements.component, clearRefinementsSettings: clearRefinements.settings, additionalData: whiteInstantSearch.additionalData })))), element)));
                    }),
                    Object.keys(whiteInstantSearch.currentRefinementsComponent).map((selector) => {
                        const currentRefinementsList = whiteInstantSearch
                            .currentRefinementsComponent[selector];
                        const elements = WhiteInstantSearch.getElements(selector);
                        const showComponent = currentRefinementsList
                            && elements
                            && currentRefinementsList.length > 0
                            && elements.length > 0;
                        if (!showComponent) {
                            return null;
                        }
                        return elements.map((element) => (createPortal(React.createElement(React.Fragment, null, currentRefinementsList.map((currentRefinements, index) => (React.createElement(CurrentRefinements, { key: `${JSON.stringify({ [selector]: index })}`, currentRefinementsComponent: currentRefinements.component, currentRefinementsSettings: currentRefinements.settings, additionalData: whiteInstantSearch.additionalData })))), element)));
                    }),
                    whiteInstantSearch.customContainers.map((customContainer) => (WhiteInstantSearch.getElements(customContainer.selector).map((element) => (createPortal(React.createElement(customContainer.container, { additionalData: whiteInstantSearch.additionalData }), element))))))));
        }
        ReactDOM.createRoot(instantSearch).render(React.createElement(RootComponent, null));
    }
    static ensureElement(elementId) {
        let element = this.getElement(`#${elementId}`);
        if (element) {
            return element;
        }
        element = document.createElement('div');
        element.setAttribute('id', elementId);
        document.body.appendChild(element);
        return element;
    }
    static getElement(selector) {
        if (!selector) {
            return null;
        }
        const element = document.querySelector(selector);
        if (element) {
            element.textContent = '';
            return element;
        }
        return null;
    }
    static getElements(selector) {
        if (!selector) {
            return [];
        }
        return [...document.querySelectorAll(selector)].map((element) => {
            const result = element;
            result.textContent = '';
            return result;
        });
    }
}
